import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";

import { useDropdownSourceContext } from "@ryde/redux";
import { IDropdownItemExt, IInput, ValidationHelpers } from "@ryde/shared";

import { getDropdownValuesLabels } from "../../../dynamic-forms";

// name will be relative to the control of form passed to the input
// can be used to create independent nested forms
export const SelectBoundInputByControl: FC<IInput> = ({
  control,
  name,
  formLabel,
  formAttributes,
  formValidation,
  className,
}) => {
  const dropItemsSource = formAttributes?.dropItemsSource;
  const customDropdownItemsFilter = formAttributes?.customDropdownItemsFilter;

  const { getDropdownItems } = useDropdownSourceContext();
  const [dropDownItems, setDropDownItems] = useState<
    IDropdownItemExt[] | undefined
  >(undefined);

  useEffect(() => {
    const getItems = async (): Promise<void> => {
      const items = await getDropdownItems?.(dropItemsSource || "unknown");
      let filteredItems = (items || []) as IDropdownItemExt[];

      if (
        customDropdownItemsFilter &&
        typeof customDropdownItemsFilter === "function"
      ) {
        filteredItems = filteredItems?.filter(customDropdownItemsFilter);
      }

      setDropDownItems(filteredItems);
    };

    if (dropItemsSource && dropItemsSource !== "unknown") {
      getItems();
    }
  }, [customDropdownItemsFilter, dropItemsSource, getDropdownItems]);

  const initial = null;

  const [optionValues, mapOptionLabel] = useMemo(
    () => getDropdownValuesLabels({ dropDownItems }),
    [dropDownItems],
  );

  const getOptionLabel = useCallback(
    (value: unknown) => {
      return mapOptionLabel.get(value as string | number) || "";
    },
    [mapOptionLabel],
  );
  const [innerValue, setInnerValue] = useState<number | null>(initial);
  const [inputValue, setInputValue] = useState<string>(
    dropDownItems?.find(item => item.value === initial)?.label || "",
  );

  return name ? (
    <Controller
      control={control}
      name={name}
      defaultValue={initial}
      render={({
        field: { value: formValue, onChange, ...restField },
        fieldState: { error },
      }) => (
        <FormControl
          required={ValidationHelpers.hasRequiredRule(formValidation)}
          fullWidth
          error={!!error}
        >
          <Autocomplete
            {...restField}
            defaultValue={initial}
            options={optionValues || []}
            value={innerValue || formValue}
            inputValue={inputValue}
            fullWidth
            className={className}
            onChange={(_, data) => {
              setInnerValue(data as number);
              onChange(data);
            }}
            onInputChange={(_, data) => {
              setInputValue(data);
            }}
            getOptionLabel={getOptionLabel}
            renderInput={params => (
              <TextField
                {...params}
                error={!!error}
                label={formLabel}
                required={ValidationHelpers.hasRequiredRule(formValidation)}
                fullWidth
              />
            )}
          />
          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  ) : null;
};
